import { useState, useEffect, useCallback, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { TrackConfig, LogMessage, KartInfo, SortMode, TeamData, RaceControlEntry, SessionTimer, RaceRules, DEFAULT_RACE_RULES, RacePlan, EMPTY_RACE_PLAN, ActiveEvent } from './types';
import { TeamInfo } from './TeamInfo';
import { applyTeamData, teamDataToTeamInfo } from './teamState';
import {
  AppendedLap,
  appendLap,
  emptyLapHistory,
  LapHistory,
  seedFromSnapshot,
  seedFromStints,
  TeamLapHistorySnapshot,
} from './analytics/lapHistory';
import {
  emptySectorHistory,
  mergeStoredLaps,
  recordSectors,
  SectorHistory,
  StoredLapRow,
} from './analytics/sectorHistory';
import { publishRemotePitBoard, RemotePitBoard } from './hooks/pitBoardStore';
import { authFetch, BACKEND_URL } from './auth/api';
import { useAuth } from './auth/AuthContext';

const SORT_STORAGE_KEY = 'karting-timing-sort';
const VALID_SORT_MODES: SortMode[] = ['position', 'average', 'average10', 'best', 'category'];

function loadSortMode(): SortMode {
  try {
    const stored = localStorage.getItem(SORT_STORAGE_KEY);
    if (stored && VALID_SORT_MODES.includes(stored as SortMode)) {
      return stored as SortMode;
    }
  } catch {
    // ignore
  }
  return 'average';
}

export interface TimingSocketReturn {
  isSocketConnected: boolean;
  isApexConnected: boolean;
  teams: Map<string, TeamInfo>;
  karts: Map<string, KartInfo>;
  messages: LogMessage[];
  raceControl: RaceControlEntry[];
  sessionTimer: SessionTimer | null;
  raceRules: RaceRules;
  /** Admin-only running order; empty for viewers, who never receive it. */
  racePlan: RacePlan;
  /** The event being recorded, needed to address its rules and plan. */
  activeEvent: ActiveEvent | null;
  /** Every lap each team has run, accumulated from `teamLapAppended`. */
  lapHistory: LapHistory;
  /** Bumped on each new lap so consumers can memoise against it. */
  lapHistoryVersion: number;
  /** Each team's sector times per lap, taken as the laps are completed. */
  sectorHistory: SectorHistory;
  /** Bumped whenever a lap's sectors are stored. */
  sectorHistoryVersion: number;
  /**
   * Fetch the sectors of laps run before this page was opened, once per race.
   * Safe to call repeatedly.
   */
  loadSectorBackfill: () => Promise<void>;
  /**
   * Bumped whenever any strategy is saved, deleted or has a stop recorded against
   * it. The notice carries ids only; consumers refetch, and the server decides
   * what each account may see.
   */
  strategyVersion: number;
  sortMode: SortMode;
  startListening: (config: TrackConfig, title: string) => void;
  stopListening: () => void;
  setSortMode: (mode: SortMode) => void;
}

export const useTimingSocket = (): TimingSocketReturn => {
  const { token, signOut } = useAuth();
  const [isSocketConnected, setIsSocketConnected] = useState(false);
  const [isApexConnected, setIsApexConnected] = useState(false);
  const [teams, setTeams] = useState<Map<string, TeamInfo>>(new Map());
  const [karts, setKarts] = useState<Map<string, KartInfo>>(new Map());
  const [messages, setMessages] = useState<LogMessage[]>([]);
  const [raceControl, setRaceControl] = useState<RaceControlEntry[]>([]);
  const [sessionTimer, setSessionTimer] = useState<SessionTimer | null>(null);
  const [raceRules, setRaceRules] = useState<RaceRules>(DEFAULT_RACE_RULES);
  const [racePlan, setRacePlan] = useState<RacePlan>(EMPTY_RACE_PLAN);
  const [activeEvent, setActiveEvent] = useState<ActiveEvent | null>(null);
  const lapHistoryRef = useRef<LapHistory>(emptyLapHistory());
  const [lapHistoryVersion, setLapHistoryVersion] = useState(0);
  const sectorHistoryRef = useRef<SectorHistory>(emptySectorHistory());
  const [sectorHistoryVersion, setSectorHistoryVersion] = useState(0);
  /** The race the sector store belongs to, and whether its backfill has been fetched. */
  const sectorEventRef = useRef<{ id: string | null; backfilled: boolean }>({
    id: null,
    backfilled: false,
  });
  const [strategyVersion, setStrategyVersion] = useState(0);
  const [sortMode, setSortMode] = useState<SortMode>(loadSortMode);

  const socketRef = useRef<Socket | null>(null);
  const currentConfigRef = useRef<TrackConfig | null>(null);
  const currentTitleRef = useRef<string | null>(null);

  const log = useCallback((type: LogMessage['type'], content: string) => {
    setMessages((prev) => [
      ...prev,
      { timestamp: new Date().toISOString(), type, content },
    ]);
  }, []);

  useEffect(() => {
    if (!token) return;

    const socket = io(BACKEND_URL, {
      transports: ['websocket', 'polling'],
      autoConnect: true,
      secure: BACKEND_URL.startsWith('https'),
      // The backend rejects the handshake outright when this is missing or stale.
      auth: { token },
    });
    socketRef.current = socket;

    // Set on every (re)connect, cleared by the first `activeEvent` that follows. The
    // server sends that message to a connecting client unconditionally, saying what
    // it is recording right now (or nothing), so it is the moment to decide whether
    // recording needs resuming.
    let resumePending = false;

    socket.on('connect', () => {
      setIsSocketConnected(true);
      log('system', 'Connected to backend');
      resumePending = true;
    });

    socket.on('connect_error', (err: Error) => {
      if (err.message === 'unauthorized') {
        signOut('Your session has expired — please sign in again');
        return;
      }
      log('error', `Backend connection error: ${err.message}`);
    });

    socket.on('disconnect', (reason) => {
      setIsSocketConnected(false);
      setIsApexConnected(false);
      log('system', `Disconnected from backend: ${reason}`);
    });

    socket.on('apexConnected', () => {
      setIsApexConnected(true);
      log('system', 'Backend connected to Apex timing server');
    });

    socket.on('apexDisconnected', (payload: { reason: string }) => {
      setIsApexConnected(false);
      log('system', `Backend disconnected from Apex: ${payload.reason}`);
    });

    socket.on('teamLapHistory', (snapshot: TeamLapHistorySnapshot[]) => {
      seedFromSnapshot(lapHistoryRef.current, snapshot);
      setLapHistoryVersion((v) => v + 1);
    });

    socket.on('teamLapAppended', (lap: AppendedLap) => {
      appendLap(lapHistoryRef.current, lap);
      setLapHistoryVersion((v) => v + 1);
    });

    socket.on('teamsSnapshot', (snapshot: TeamData[]) => {
      for (const data of snapshot) {
        seedFromStints(lapHistoryRef.current, data.id, data.stintAverages ?? []);
        // Only primes the lap counters; a snapshot is not a lap finishing.
        recordSectors(sectorHistoryRef.current, data, Date.now());
      }
      setLapHistoryVersion((v) => v + 1);

      setTeams(() => {
        const newMap = new Map<string, TeamInfo>();
        for (const data of snapshot) {
          newMap.set(data.id, teamDataToTeamInfo(data));
        }
        return newMap;
      });
    });

    socket.on('teamUpdate', (data: TeamData) => {
      if (recordSectors(sectorHistoryRef.current, data, Date.now())) {
        setSectorHistoryVersion((v) => v + 1);
      }
      setTeams((prev) => {
        const newMap = new Map(prev);
        const existing = newMap.get(data.id);
        if (existing) {
          applyTeamData(existing, data);
          newMap.set(data.id, existing);
        } else {
          newMap.set(data.id, teamDataToTeamInfo(data));
        }
        return newMap;
      });
    });

    socket.on('kartUpdate', (data: KartInfo) => {
      setKarts((prev) => {
        const newMap = new Map(prev);
        newMap.set(data.number, data);
        return newMap;
      });
    });

    socket.on('log', (message: LogMessage) => {
      setMessages((prev) => [...prev, message]);
    });

    socket.on('raceControl', (entries: RaceControlEntry[]) => {
      setRaceControl(entries);
    });

    socket.on('sessionTimer', (timer: SessionTimer) => {
      setSessionTimer(timer);
    });

    socket.on('raceRules', (rules: RaceRules) => {
      setRaceRules(rules);
    });

    socket.on('racePlan', (plan: RacePlan) => {
      setRacePlan(plan ?? EMPTY_RACE_PLAN);
    });

    socket.on('strategyChanged', () => {
      setStrategyVersion((v) => v + 1);
    });

    socket.on('activeEvent', (event: ActiveEvent | null) => {
      setActiveEvent(event);

      // Sectors belong to one race: a different one starts the store over.
      if ((event?.id ?? null) !== sectorEventRef.current.id) {
        sectorEventRef.current = { id: event?.id ?? null, backfilled: false };
        sectorHistoryRef.current = emptySectorHistory();
        setSectorHistoryVersion((v) => v + 1);
      }

      if (!resumePending) return;
      resumePending = false;

      // Resume only if the server has forgotten the race, as after a restart. When
      // it is still recording, this is just the connection coming back (a laptop
      // waking, wifi switching) and sending Start again would end the race in
      // progress and open a new event, cutting one race into pieces. Stopping and
      // starting on purpose still works: that goes through `startListening`.
      if (event === null && currentConfigRef.current && currentTitleRef.current) {
        socket.emit('start', {
          config: currentConfigRef.current,
          title: currentTitleRef.current,
        });
        log('system', 'Backend was not recording; resumed listening');
      }
    });

    // Handed to the pit-board store rather than held in state: the board is
    // owned by usePitAssignments, and two copies would drift.
    socket.on('pitBoard', (payload: RemotePitBoard) => {
      if (payload?.board) publishRemotePitBoard(payload);
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [log, token, signOut]);

  const startListening = useCallback((config: TrackConfig, title: string) => {
    currentConfigRef.current = config;
    currentTitleRef.current = title;
    setTeams(new Map());
    setKarts(new Map());
    setRaceControl([]);
    setSessionTimer(null);
    setRaceRules(DEFAULT_RACE_RULES);
    setRacePlan(EMPTY_RACE_PLAN);
    lapHistoryRef.current = emptyLapHistory();
    setLapHistoryVersion(0);
    sectorHistoryRef.current = emptySectorHistory();
    sectorEventRef.current = { ...sectorEventRef.current, backfilled: false };
    setSectorHistoryVersion(0);
    if (socketRef.current?.connected) {
      socketRef.current.emit('start', { config, title });
      log('system', `Requested start "${title}" for track: ${config.name}`);
    } else {
      log('error', 'Cannot start: not connected to backend');
    }
  }, [log]);

  const stopListening = useCallback(() => {
    currentConfigRef.current = null;
    currentTitleRef.current = null;
    if (socketRef.current?.connected) {
      socketRef.current.emit('stop');
      log('system', 'Stopped listening to Apex');
    }
    setIsApexConnected(false);
  }, [log]);

  const loadSectorBackfill = useCallback(async () => {
    const state = sectorEventRef.current;
    if (!state.id || state.backfilled) return;
    state.backfilled = true;
    const eventId = state.id;

    try {
      const PAGE = 20000;
      const rows: StoredLapRow[] = [];
      for (let offset = 0; ; offset += PAGE) {
        const res = await authFetch(
          `/events/${encodeURIComponent(eventId)}/laps?limit=${PAGE}&offset=${offset}`,
        );
        const page = (await res.json()) as StoredLapRow[];
        rows.push(...page);
        if (page.length < PAGE) break;
      }
      // The race may have changed while the request was out.
      if (sectorEventRef.current.id !== eventId) return;
      if (mergeStoredLaps(sectorHistoryRef.current, rows) > 0) {
        setSectorHistoryVersion((v) => v + 1);
      }
    } catch (err) {
      // Let a later attempt try again; live collection carries on regardless.
      if (sectorEventRef.current.id === eventId) sectorEventRef.current.backfilled = false;
      log('error', `Could not load earlier sector times: ${err instanceof Error ? err.message : err}`);
    }
  }, [log]);

  const handleSetSortMode = useCallback((mode: SortMode) => {
    setSortMode(mode);
    try {
      localStorage.setItem(SORT_STORAGE_KEY, mode);
    } catch {
      // ignore
    }
    log('system', `Sorting by ${mode}`);
  }, [log]);

  return {
    isSocketConnected,
    isApexConnected,
    teams,
    karts,
    messages,
    raceControl,
    sessionTimer,
    raceRules,
    racePlan,
    activeEvent,
    lapHistory: lapHistoryRef.current,
    lapHistoryVersion,
    sectorHistory: sectorHistoryRef.current,
    sectorHistoryVersion,
    loadSectorBackfill,
    strategyVersion,
    sortMode,
    startListening,
    stopListening,
    setSortMode: handleSetSortMode,
  };
};
